import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiEdit, FiEye } from 'react-icons/fi';
import { getClients } from '../api';
import EditClientModal from '../components/EditClientModal';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';

const ClientListPage = () => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingClientId, setEditingClientId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const { user } = useCurrentUser();
  const canCreateClient = hasPermission(user, 'client.create');
  const canUpdateClient = hasPermission(user, 'client.update');
  const canReadClient = hasPermission(user, 'client.read');
  const [expandedClients, setExpandedClients] = useState(new Set());
  const pageSize = 12;

  const fetchClients = async () => {
    setLoading(true);
    setError('');
    try {
      const trimmedSearch = searchTerm.trim();
      const data = await getClients({
        page,
        pageSize,
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
      });
      const normalized = normalizePaginatedResponse(data);
      setClients(normalized.data || []);
      setTotalClients(normalized.total || 0);
      setTotalPages(
        normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / pageSize))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [page, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleEditClient = (client) => {
    if (!canUpdateClient) return;
    setEditingClientId(client.id);
  };

  const handleModalClose = () => {
    setEditingClientId(null);
  };

  const handleClientUpdated = async () => {
    await fetchClients();
  };

  const toggleClientDetails = (clientId) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  return (
    <>
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold">Clientes</h1>
            <p className="text-sm text-gray-600">
              Administra datos de contacto y gestion de clientes.
            </p>
          </div>
          {canCreateClient && (
            <Link
              to="/client/new"
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 inline-flex items-center gap-2"
            >
              Nuevo Cliente
            </Link>
          )}
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
          <input
            type="text"
            placeholder="Buscar por nombre, rut, giro o correo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <span className="px-2 py-1 rounded bg-blue-100 text-blue-700">
              Total: {totalClients}
            </span>
            <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700">
              En pagina: {clients.length}
            </span>
          </div>
        </div>

        {error && <p className="text-red-600 mb-4">{error}</p>}

        {loading ? (
          <p className="text-gray-600">
            <InlineLoading />
          </p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.length === 0 ? (
              <li className="text-gray-600">No hay clientes para mostrar.</li>
            ) : (
              clients.map((client) => {
                const clientName = client.name || 'Sin nombre';
                const isExpanded = expandedClients.has(client.id);
                const detailsId = `client-${client.id}-details`;
                return (
                  <li
                    key={client.id}
                    className="border border-blue-100 rounded-xl p-4 shadow-sm bg-gradient-to-br from-white via-white to-emerald-50 hover:shadow-md transition-shadow"
                  >
                    <button
                      type="button"
                      onClick={() => toggleClientDetails(client.id)}
                      className={`w-full text-left rounded-lg p-3 transition-colors ${
                        isExpanded ? 'bg-blue-50/70' : 'bg-white/70'
                      }`}
                      aria-expanded={isExpanded}
                      aria-controls={detailsId}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{clientName}</h3>
                          <p className="text-xs text-gray-500 mt-1">
                            Rut: {client.rut || '—'}
                          </p>
                        </div>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {isExpanded ? 'Ocultar' : 'Ver mas'}
                        </span>
                      </div>
                    </button>

                    {isExpanded && (
                      <div id={detailsId} className="mt-3 space-y-2 text-sm text-gray-700">
                        {client.giro && (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                              Giro: {client.giro}
                            </span>
                          </div>
                        )}
                        <p>Email: {client.email || '—'}</p>
                        <p>Telefono: {client.phone || '—'}</p>
                        <p>Direccion: {client.address || '—'}</p>
                        <p>Ciudad: {client.city || '—'}</p>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-blue-100">
                      {canUpdateClient && (
                        <button
                          onClick={() => handleEditClient(client)}
                          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center gap-2"
                        >
                          <FiEdit />
                          Editar
                        </button>
                      )}
                      {canReadClient && (
                        <Link
                          to={`/client/${client.id}`}
                          className="bg-emerald-500 text-white px-4 py-2 rounded hover:bg-emerald-600 flex items-center gap-2"
                        >
                          <FiEye />
                          Ver detalle
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        )}

        {totalClients > pageSize && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500 mt-6">
            <div>
              Mostrando {totalClients === 0 ? 0 : (page - 1) * pageSize + 1}-
              {Math.min(page * pageSize, totalClients)} de {totalClients}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
              >
                Anterior
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {editingClientId && (
        <EditClientModal
          clientId={editingClientId}
          onClose={handleModalClose}
          onUpdated={handleClientUpdated}
        />
      )}
    </>
  );
};

export default ClientListPage;
